import { useMutation, useQuery, useQueryClient } from "react-query";
import { createTimeOffRequest, deleteTimeOffRequest, fetchTimeOffRequests, updateTimeOffRequest, } from "@/features/timeOff/services/timeOffService";
const useTimeOffRequests = (page = 1, filters = {}, limit = 10, sort) => {
    const queryClient = useQueryClient();
    const queryKey = ["timeOffRequests", { page, filters, limit, sort }];
    const { data, error, isLoading, refetch, } = useQuery(queryKey, () => fetchTimeOffRequests(page, filters, limit, sort), {
        keepPreviousData: true,
        staleTime: 60000,
    });
    const addMutation = useMutation((payload) => createTimeOffRequest(payload), {
        onSuccess: () => queryClient.invalidateQueries(["timeOffRequests"]),
    });
    const updateMutation = useMutation((payload) => updateTimeOffRequest(payload), {
        onSuccess: () => queryClient.invalidateQueries(["timeOffRequests"]),
    });
    const deleteMutation = useMutation((id) => deleteTimeOffRequest(id), {
        onSuccess: () => {
            queryClient.invalidateQueries(["timeOffRequests"]);
            queryClient.invalidateQueries(["leaveBalances"]);
        },
    });
    const decideMutation = useMutation((payload) => updateTimeOffRequest(payload), {
        onMutate: async (variables) => {
            await queryClient.cancelQueries(queryKey);
            const previousData = queryClient.getQueryData(queryKey);
            const optimisticDecision = {
                status: variables.status,
                decided_at: variables.decided_at ?? new Date().toISOString(),
                manager: variables.manager ?? "Manager",
                comment: variables.reason ?? null,
            };
            queryClient.setQueryData(queryKey, (old) => {
                if (!old?.timeOffRequests)
                    return old;
                return {
                    ...old,
                    timeOffRequests: old.timeOffRequests.map((request) => request.id === variables.id
                        ? {
                            ...request,
                            status: variables.status,
                            reason: variables.reason ?? request.reason,
                            decisions: [...(request.decisions ?? []), optimisticDecision],
                        }
                        : request),
                };
            });
            return { previousData };
        },
        onError: (_error, _variables, context) => {
            if (context?.previousData) {
                queryClient.setQueryData(queryKey, context.previousData);
            }
        },
        onSettled: (_data, _error, variables) => {
            queryClient.invalidateQueries(["timeOffRequests"]);
            if (variables?.status === "approved") {
                queryClient.invalidateQueries(["leaveBalances"]);
            }
        },
    });
    return {
        requests: data?.timeOffRequests ?? [],
        meta: data?.meta ?? null,
        isLoading,
        error: error ? error.message : null,
        refetch,
        addRequest: addMutation.mutateAsync,
        updateRequest: updateMutation.mutateAsync,
        deleteRequest: deleteMutation.mutateAsync,
        decideRequest: decideMutation.mutateAsync,
    };
};
export default useTimeOffRequests;
